import { css, html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import type { StatFormat } from '@/components/Stat/types'
import { safeCustomElement } from '@/utilities/custom-element'
import type { StatsGroupSize, StatsGroupTextAlign } from './types'

@safeCustomElement('mdc-stats-group')
export class MdcStatsGroup extends LitElement {
  @property({ type: String, attribute: 'text-align' }) textAlign: StatsGroupTextAlign = 'center'
  @property({ type: String }) size: StatsGroupSize = 'md'
  @property({ type: String }) format: StatFormat = 'default'

  static styles = css`
		:host {
			display: block;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-stats-group {
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));
			width: 100%;
			justify-items: center;
		}

		.mdc-stats-group--size-sm {
			gap: var(--spacing-050, 0.5rem);
			--_mdc-stat-padding: 0 var(--spacing-012-and-half, 0.125rem);
			--_mdc-stat-gap: var(--spacing-012-and-half, 0.125rem);
			--_mdc-stat-value-size: var(--text-075, 0.75rem);
			--_mdc-stat-label-size: var(--text-062-and-half, 0.625rem);
			--_mdc-stat-icon-size: 0.75rem;
			--_mdc-stat-value-row-gap: var(--spacing-012-and-half, 0.125rem);
		}

		.mdc-stats-group--size-md {
			gap: var(--spacing-075, 0.75rem);
			--_mdc-stat-padding: 0 var(--spacing-025, 0.25rem);
			--_mdc-stat-gap: var(--spacing-025, 0.25rem);
			--_mdc-stat-value-size: var(--text-087-and-half, 0.875rem);
			--_mdc-stat-label-size: var(--text-075, 0.75rem);
			--_mdc-stat-icon-size: 0.875rem;
			--_mdc-stat-value-row-gap: var(--spacing-012-and-half, 0.125rem);
		}

		.mdc-stats-group--size-lg {
			gap: var(--spacing-100, 1rem);
			--_mdc-stat-padding: 0 var(--spacing-050, 0.5rem);
			--_mdc-stat-gap: var(--spacing-050, 0.5rem);
			--_mdc-stat-value-size: var(--text-125, 1.25rem);
			--_mdc-stat-label-size: var(--text-075, 0.75rem);
			--_mdc-stat-icon-size: 1.25rem;
			--_mdc-stat-value-row-gap: var(--spacing-025, 0.25rem);
		}

		.mdc-stats-group--size-xl {
			gap: var(--spacing-125, 1.25rem);
			--_mdc-stat-padding: 0 var(--spacing-050, 0.5rem);
			--_mdc-stat-gap: var(--spacing-050, 0.5rem);
			--_mdc-stat-value-size: var(--text-175, 1.75rem);
			--_mdc-stat-label-size: var(--text-087-and-half, 0.875rem);
			--_mdc-stat-icon-size: 1.5rem;
			--_mdc-stat-value-row-gap: var(--spacing-050, 0.5rem);
		}

		.mdc-stats-group--size-2xl {
			gap: var(--spacing-150, 1.5rem);
			--_mdc-stat-padding: 0 var(--spacing-075, 0.75rem);
			--_mdc-stat-gap: var(--spacing-050, 0.5rem);
			--_mdc-stat-value-size: var(--text-250, 2.5rem);
			--_mdc-stat-label-size: var(--text-100, 1rem);
			--_mdc-stat-icon-size: 2rem;
			--_mdc-stat-value-row-gap: var(--spacing-050, 0.5rem);
		}

		.mdc-stats-group--size-3xl {
			gap: var(--spacing-200, 2rem);
			--_mdc-stat-padding: 0 var(--spacing-100, 1rem);
			--_mdc-stat-gap: var(--spacing-075, 0.75rem);
			--_mdc-stat-value-size: var(--text-300, 3rem);
			--_mdc-stat-label-size: var(--text-125, 1.25rem);
			--_mdc-stat-icon-size: 2.5rem;
			--_mdc-stat-value-row-gap: var(--spacing-075, 0.75rem);
		}

		.mdc-stats-group--text-align-left {
			--_mdc-stat-align-items: flex-start;
			--_mdc-stat-text-align: left;
		}

		.mdc-stats-group--text-align-center {
			--_mdc-stat-align-items: center;
			--_mdc-stat-text-align: center;
		}
	`

  connectedCallback() {
    super.connectedCallback()
    this._updateChildSizes()
  }

  updated(changedProperties: Map<PropertyKey, unknown>) {
    super.updated(changedProperties)
    if (changedProperties.has('size') || changedProperties.has('format')) {
      this._updateChildSizes()
    }
  }

  private _updateChildSizes() {
    const stats = this.querySelectorAll('mdc-stat')
    stats.forEach((stat) => {
      stat.setAttribute('size', this.size)
      if (this.format !== 'default') {
        stat.setAttribute('format', this.format)
      }
    })
  }

  render() {
    const className = `mdc-stats-group mdc-stats-group--text-align-${this.textAlign} mdc-stats-group--size-${this.size}`

    return html`
			<div class=${className}>
				<slot></slot>
			</div>
		`
  }
}
